import React, { FC, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  BookOpen,
  Bot,
  DoorOpen,
  ScrollText,
  UserPlus,
  Users,
} from 'lucide-react';
import { useQuickPlay } from './hooks';
import { CREATE_ROOM_ROUTE } from '../create-room';
import { Store } from '../../../redux';
import { IN_PLAY_ROUTE } from '../in-play';
import { INVITE_PLAYER_ROUTE } from '../invite-players';
import { COINS_INFO_ROUTE } from '../coins-info';
import { usePushNotifications } from '../../../shared/lib';
import { CoinRush } from './components/coin-rush';
import { Screen, ScreenTabs } from '@/shared/components/ios-screen';
import { GAME_RULES_ROUTES, TOC_ROUTES } from '../../unauthenticated';
import {
  CoinPile,
  LoadingLayout,
  NotEnoughCoins,
  useNotEnoughCoins,
} from '@/shared/components';
import { ShowDeniedNotification } from './components/show-denied-notification';
import { ProgressionBar } from './components/progression-bar';
import { FriendsActivity } from './components/friends-activity';
import { TutorialOverlay } from './components/tutorial-overlay';
import { useHttpQuery } from '@/api';
import { API_URLS } from '../consts';

// A first-time player has 0 games played in progression
interface ProgressionData {
  gamesPlayed: number;
}

export const TUTORIAL_STORAGE_KEY = 'bhabi_tutorial_seen';

export const QUICK_PLAY_MESSAGES = [
  'Setting up your table…',
  'Seating the bots…',
  'Dealing the cards…',
];

type HomeTab = 'progress' | 'friends' | 'coins';

/** Friends tab: who played today, the invite reward and a way to find players. */
export const FriendsPanel: FC<{ userID: string }> = ({ userID }) => (
  <div className="bh-panel">
    <FriendsActivity userID={userID} />
    <p className="bh-small">
      <span aria-hidden="true">🎁 </span>
      Invite a friend — you{' '}
      <strong style={{ color: 'var(--bh-accent-text)' }}>
        both get 50 coins
      </strong>{' '}
      when they finish their first game.
    </p>
    <Link
      to={`${INVITE_PLAYER_ROUTE.ABSOLUTE_PATH}?tab=Users`}
      className="bh-btn bh-btn--secondary bh-btn--small"
    >
      <Users size={18} aria-hidden="true" />
      Find players
    </Link>
  </div>
);

/** Coins tab: balance, the wallet and the Daily-deal entry (wallet's pill). */
export const CoinsPanel: FC<{ coins: number; userID: string }> = ({
  coins,
  userID,
}) => (
  <div className="bh-panel">
    <div className="bh-hs__stat">
      <div className="bh-coins__balance">
        <CoinPile size="md" />
        <div>
          <h2 className="bh-h3">Coins</h2>
          <p className="bh-coins__value" aria-live="polite">
            {coins}
          </p>
        </div>
      </div>
      <Link
        to={COINS_INFO_ROUTE.ABSOLUTE_PATH}
        className="bh-btn bh-btn--secondary bh-btn--small"
      >
        Open wallet
      </Link>
    </div>
    <p className="bh-small bh-muted">
      Tables cost coins to deal. Pick up the Daily deal and win games to top up.
    </p>
    <CoinRush userID={userID} />
  </div>
);

export const HomePage: FC = () => {
  const { quickPlay, isError, isLoading } = useQuickPlay();
  const nav = useNavigate();
  const { isInRoom, userId, name, coins } = useSelector((state: Store) => ({
    isInRoom: state.userReducer.isInRoom,
    userId: state.userReducer.id,
    name: state.userReducer.name,
    coins: state.userReducer.coins,
  }));

  const {
    showCoinsMessage,
    checkIfEnoughCoins,
    hasEnoughCoins,
    setShowCoinsMessage,
  } = useNotEnoughCoins();

  const [loadingMessage, setLoadingMessage] = useState(QUICK_PLAY_MESSAGES[0]);
  const [showTutorial, setShowTutorial] = useState(false);

  // Fetch progression to detect first-time players
  const { data: progressionData } = useHttpQuery<ProgressionData>(
    ['userProgression', userId].join('-'),
    API_URLS.USER_PROGRESSION,
    'GET'
  );

  // Show tutorial once for first-time players (0 games played)
  useEffect(() => {
    if (!progressionData) return;
    const alreadySeen = localStorage.getItem(TUTORIAL_STORAGE_KEY);
    if (progressionData.gamesPlayed === 0 && !alreadySeen) {
      setShowTutorial(true);
    }
  }, [progressionData]);

  const handleDismissTutorial = React.useCallback(() => {
    localStorage.setItem(TUTORIAL_STORAGE_KEY, '1');
    setShowTutorial(false);
  }, []);

  useEffect(() => {
    if (isLoading && !isError) return;
    if (isInRoom) {
      nav(IN_PLAY_ROUTE.ABSOLUTE_PATH);
    }
  }, [isError, isInRoom, isLoading, nav]);

  useEffect(() => {
    if (!isLoading) return;

    let currentIndex = 0;
    setLoadingMessage(QUICK_PLAY_MESSAGES[0]);

    const interval = setInterval(() => {
      currentIndex = (currentIndex + 1) % QUICK_PLAY_MESSAGES.length;
      setLoadingMessage(QUICK_PLAY_MESSAGES[currentIndex]);
    }, 2000);

    return () => clearInterval(interval);
  }, [isLoading]);

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<HomeTab>('progress');

  const showNotificationDeniedPopup = () => {
    setOpen(true);
  };

  usePushNotifications(showNotificationDeniedPopup);

  const isBusy = !isError && isLoading;

  const handleQuickPlayClick = () => {
    if (!hasEnoughCoins) {
      checkIfEnoughCoins();
      return;
    }
    quickPlay();
  };

  const firstName = (name || '').trim().split(/\s+/)[0];
  const showName = !!firstName && firstName.toLowerCase() !== 'anonymous';

  return (
    <>
      {showTutorial && <TutorialOverlay onDismiss={handleDismissTutorial} />}

      {isBusy && (
        <LoadingLayout
          text={loadingMessage}
          hint="Quick play seats you with four bots."
        />
      )}
      <ShowDeniedNotification open={open} setOpen={setOpen} />

      {/* Insufficient Coins Message */}
      <NotEnoughCoins
        showCoinsMessage={showCoinsMessage}
        setShowCoinsMessage={setShowCoinsMessage}
      />

      <Screen className="bh-hs">
        <section
          className="bh-surface bh-surface--felt bh-hs__hero"
          aria-labelledby="home-title"
        >
          <div className="bh-hs__greet">
            <div>
              <p className="bh-eyebrow">Your table is waiting</p>
              <h1 id="home-title" className="bh-screen__title">
                {showName ? (
                  <>
                    Sat sri akal, <em>{firstName}</em>
                  </>
                ) : (
                  <>
                    Deal a <em>table.</em>
                  </>
                )}
              </h1>
              <p className="bh-lead bh-hs__lead">
                Start a private table and invite your friends — or warm up
                against bots.
              </p>
            </div>
            <div className="bh-mini-fan bh-hs__fan" aria-hidden="true">
              <span>A</span>
              <span data-red="true">♥</span>
              <span>♣</span>
              <span data-red="true">♦</span>
            </div>
          </div>

          <div className="bh-hs__actions">
            <button
              type="button"
              className="bh-btn bh-btn--primary bh-btn--block"
              onClick={() => {
                nav(CREATE_ROOM_ROUTE.ABSOLUTE_PATH);
              }}
            >
              <UserPlus size={20} aria-hidden="true" />
              Create a table &amp; invite
            </button>
            <div className="bh-hs__secondary">
              <button
                type="button"
                className="bh-btn bh-btn--secondary bh-btn--small"
                onClick={handleQuickPlayClick}
                disabled={isBusy}
                aria-busy={isBusy}
              >
                <Bot size={18} aria-hidden="true" />
                Quick play
              </button>
              <Link
                to={`${INVITE_PLAYER_ROUTE.ABSOLUTE_PATH}?tab=Rooms`}
                className="bh-btn bh-btn--secondary bh-btn--small"
              >
                <DoorOpen size={18} aria-hidden="true" />
                Rejoin
              </Link>
            </div>
          </div>
        </section>

        <ScreenTabs
          className="bh-hs__tabs"
          label="Your progress, friends and coins"
          value={tab}
          onChange={id => setTab(id as HomeTab)}
          tabs={[
            {
              id: 'progress',
              label: 'Progress',
              content: <ProgressionBar userID={userId} />,
            },
            {
              id: 'friends',
              label: 'Friends',
              content: <FriendsPanel userID={userId} />,
            },
            {
              id: 'coins',
              label: 'Coins',
              badge: coins || 0,
              content: <CoinsPanel coins={coins || 0} userID={userId} />,
            },
          ]}
        />

        <nav className="bh-hs__dock" aria-label="More">
          <Link
            to={GAME_RULES_ROUTES.ABSOLUTE_PATH}
            className="bh-btn bh-btn--ghost bh-btn--small"
          >
            <BookOpen size={18} aria-hidden="true" />
            Rules
          </Link>
          <Link
            to={TOC_ROUTES.ABSOLUTE_PATH}
            className="bh-btn bh-btn--ghost bh-btn--small"
          >
            <ScrollText size={18} aria-hidden="true" />
            Terms
          </Link>
        </nav>
      </Screen>
    </>
  );
};
